import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Eye,
  DollarSign,
  Scale,
  FileText,
  Car,
  Radio,
  Camera,
  Clock,
  Target,
  TrendingUp,
  XCircle,
  BarChart3,
  Landmark,
  Search,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#002B7F]/10 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#002B7F]/5 transition-colors"
      >
        <span className="font-bold text-[#002B7F] pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#C0C0C0] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const faqs = [
  {
    question: "Is this anti-police?",
    answer:
      "No. This reform is designed to support law enforcement by freeing sworn officers from routine administrative enforcement so they can focus on what they are trained for — responding to violent crime, conducting investigations, and protecting communities. Sworn officers remain responsible for all high-risk and criminal enforcement. The TNV division handles only low-risk, routine traffic enforcement.",
  },
  {
    question: "Are TNV officers safe without weapons?",
    answer:
      "TNV officers handle only routine traffic violations — expired registration, equipment issues, insurance flags. They do not conduct searches, pursue suspects, or handle violent situations. Any situation involving suspicion of criminal activity, DUI, drugs, or violent behavior is immediately escalated to sworn armed officers. The role is structurally comparable to parking enforcement and municipal code enforcement — roles that already operate safely and unarmed in cities across the country.",
  },
  {
    question: "What happens if a TNV officer encounters a dangerous situation?",
    answer:
      "TNV officers are trained to identify escalation indicators and immediately request sworn officer response. They do not engage. They do not conduct searches. They do not pursue. Their mandate is strictly limited to routine enforcement, and any deviation triggers immediate handoff to armed law enforcement personnel.",
  },
  {
    question: "Will this reduce police jobs?",
    answer:
      "No. This creates a new category of public safety employment while allowing sworn officers to be redeployed to higher-priority assignments. Departments maintain their sworn officer staffing — those officers simply spend more time on violent crime, investigations, and emergency response instead of routine traffic stops.",
  },
  {
    question: "How does the technology prevent bias?",
    answer:
      "The vehicle-mounted systems scan license plates and identify objective violations — expired registration, insurance flags, equipment issues — before the officer makes any contact. The system determines the violation basis, not the individual officer. Officers cannot add violations not identified by the system or directly observed facts, and they cannot remove violations. Every stop is logged, time-stamped, and auditable. This removes discretionary stop selection from the process entirely.",
  },
  {
    question: "Does this already exist anywhere?",
    answer:
      "Several jurisdictions have explored or piloted civilian traffic enforcement programs. The concept builds on existing models like parking enforcement, red-light camera programs, and municipal code enforcement — all of which use unarmed civilian personnel for routine regulatory enforcement. The TNV model formalizes and scales this approach with modern technology and clear escalation protocols.",
  },
];

const TNVPage = () => {
  return (
    <div data-testid="tnv-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-28 bg-[#002B7F]">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Shield className="w-4 h-4" />
            Public Safety Reform
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6 max-w-3xl">
            Traffic & Non-Violent Enforcement (TNV)
          </h1>
          <p className="text-lg text-[#C0C0C0] max-w-2xl mb-8">
            A professional, tiered enforcement system that assigns routine
            traffic enforcement to trained unarmed officers — so sworn armed
            officers can focus on violent crime, investigations, and emergency
            response.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-structure"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* ── SECTION 1: THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            The Problem
          </h2>

          <p className="text-slate-600 leading-relaxed mb-4">
            Over 20 million traffic stops occur annually in the United States.
            Traffic stops are one of the most common interactions between the
            public and law enforcement — and while the vast majority are
            routine, they are a known entry point for escalation incidents.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-red-50 border border-red-200 p-5">
              <p className="text-3xl font-black text-red-700 mb-1">~100</p>
              <p className="text-red-800 font-bold text-sm mb-1">
                Civilian Deaths Per Year
              </p>
              <p className="text-red-700 text-xs">
                Associated with traffic-stop-related encounters nationwide
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 p-5">
              <p className="text-3xl font-black text-red-700 mb-1">5–10</p>
              <p className="text-red-800 font-bold text-sm mb-1">
                Officer Deaths Per Year
              </p>
              <p className="text-red-700 text-xs">
                Occurring in traffic-stop-related incidents nationwide
              </p>
            </div>
          </div>

          <p className="text-slate-600 leading-relaxed mb-4">
            These incidents are rare relative to the total volume of stops. But
            they are concentrated in a specific type of interaction — routine
            stops involving discretion and escalation potential.
          </p>

          <div className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-5 mb-6">
            <p className="text-slate-500 text-xs">
              <strong>Sources:</strong> Federal Bureau of Investigation; U.S.
              Department of Justice; National Law Enforcement Officers Memorial
              Fund
            </p>
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold">
              "Over 20 million traffic stops per year. Most are routine. But the
              current system treats every one as a potential high-risk
              encounter."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: WHY CHANGE IS NEEDED ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            Why Change Is Needed
          </h2>

          <p className="text-slate-600 leading-relaxed mb-4">
            Armed sworn officers are frequently assigned to low-risk traffic
            enforcement. This is not the most efficient use of trained
            personnel. It reduces the time available for violent crime response
            and investigations. And it contributes to unnecessary workload and
            overtime costs.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              {
                icon: Clock,
                title: "Time Spent on Routine Stops",
                desc: "Sworn officers spend significant portions of their shifts on administrative traffic enforcement instead of responding to serious crime",
              },
              {
                icon: DollarSign,
                title: "Overtime and Cost Burden",
                desc: "Traffic enforcement is a major source of overtime costs, diverting budget from critical public safety priorities",
              },
              {
                icon: Shield,
                title: "Reduced Availability for Serious Crime",
                desc: "Every hour spent on a routine traffic stop is an hour not spent investigating violent crime or responding to emergencies",
              },
              {
                icon: Target,
                title: "Mismatch of Training to Task",
                desc: "Officers trained for high-risk situations are assigned to low-risk administrative enforcement — an inefficient allocation of expertise",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex items-start gap-3 p-5 bg-white border-2 border-[#002B7F]/10"
                >
                  <Icon className="w-5 h-5 text-[#002B7F] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-[#002B7F] text-sm">
                      {item.title}
                    </p>
                    <p className="text-slate-600 text-xs mt-1">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#D4AF37]/15 border-2 border-[#D4AF37]/30 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              Highly trained professionals are being used for routine
              administrative enforcement tasks instead of critical public safety
              functions.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: TNV DIVISION STRUCTURE ── */}
      <section
        id="the-structure"
        className="py-16 md:py-24 bg-white scroll-mt-32"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Users className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              TNV Division Structure
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A tiered enforcement system aligned with risk level — routine
              traffic handled by TNV officers, serious crime handled by sworn
              officers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* TNV Officers */}
            <div className="bg-[#002B7F] p-6">
              <h3 className="font-bold text-[#D4AF37] mb-4">TNV Officers</h3>
              <div className="space-y-3">
                {[
                  "Unarmed enforcement personnel",
                  "Enforce traffic laws and non-violent vehicle regulations only",
                  "Do not conduct searches",
                  "Do not handle suspected violent or drug-related crime",
                  "Must call sworn officers when escalation is required",
                  "Starting salary: ~$48,000–$55,000",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sworn Officers */}
            <div className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-6">
              <h3 className="font-bold text-[#002B7F] mb-4">
                Sworn Officers (e.g., LVMPD)
              </h3>
              <div className="space-y-3">
                {[
                  "Armed law enforcement personnel",
                  "Handle all violent crime, investigations, emergencies",
                  "Respond to TNV escalation requests",
                  "Conduct searches, arrests, and criminal enforcement",
                  "Focus on high-risk, high-priority public safety",
                  "Starting salary: ~$66,000+ (with overtime and benefits)",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-[#002B7F] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold">
              "The right level of response for the right situation."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: TECHNOLOGY-ASSISTED ENFORCEMENT ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Camera className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Technology-Assisted Objective Enforcement
            </h2>
            <p className="text-[#C0C0C0] max-w-2xl mx-auto">
              Enforcement based on objective, verifiable criteria — not
              individual discretion.
            </p>
          </div>

          <div className="bg-white/10 border border-white/20 p-6 mb-8">
            <p className="text-white leading-relaxed mb-4">
              Vehicles used in TNV enforcement will be equipped with systems
              that scan license plates, identify objective violations —
              expired registration, insurance flags, equipment issues — and
              log the violation basis prior to any officer interaction.
            </p>
            <p className="text-white/80 leading-relaxed">
              This ensures enforcement is based on objective criteria rather
              than discretionary selection. The system determines the violation
              basis, not the individual officer.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                icon: Search,
                title: "Automatic Violation Detection",
                desc: "License plate scanning identifies registration, insurance, and equipment violations before any stop occurs",
              },
              {
                icon: FileText,
                title: "Pre-Logged Violation Basis",
                desc: "Every stop reason is recorded in the system before the officer makes contact — creating a clear, auditable record",
              },
              {
                icon: Eye,
                title: "No Additions or Removals",
                desc: "TNV officers cannot add violations not identified by the system or directly observed facts, and cannot remove violations",
              },
              {
                icon: Clock,
                title: "Time-Stamped Audit Trail",
                desc: "Every stop is logged, time-stamped, and fully auditable — creating complete transparency and accountability",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white/10 border border-white/20 p-5"
                >
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-white/10 border-2 border-[#D4AF37]/40 p-6 text-center">
            <p className="text-[#D4AF37] font-bold text-lg">
              "The system determines the violation basis, not the individual
              officer."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: SAFETY OUTCOMES ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            Safety Outcomes
          </h2>

          <p className="text-slate-600 leading-relaxed mb-4">
            Traffic stops number over 20 million annually in the United States.
            Approximately 100 civilian deaths per year are associated with
            traffic-stop-related encounters. Approximately 5–10 officer deaths
            per year nationwide are related to traffic-stop-related incidents.
          </p>

          <p className="text-slate-600 leading-relaxed mb-8">
            The TNV division is structured to avoid high-risk situations
            entirely. TNV officers will not engage in escalated enforcement
            scenarios. All higher-risk interactions are handled by sworn
            officers.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "No Armed Encounters",
                desc: "TNV officers are unarmed and do not engage in situations involving weapons, violence, or criminal suspects",
              },
              {
                title: "Mandatory Escalation Protocol",
                desc: "Any suspicion of DUI, drugs, violent behavior, or criminal activity triggers immediate sworn officer response",
              },
              {
                title: "Comparable to Existing Roles",
                desc: "The safety profile is similar to parking enforcement and municipal code enforcement — roles that already operate safely and unarmed",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-emerald-50 border border-emerald-200 p-5"
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 mb-2" />
                <h3 className="font-bold text-emerald-800 text-sm mb-1">
                  {item.title}
                </h3>
                <p className="text-emerald-700 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold">
              "TNV officers handle routine enforcement. Sworn officers handle
              serious threats. Each role is designed for its specific risk
              level."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: COST AND EFFICIENCY ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <DollarSign className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Cost and Efficiency Impact
            </h2>
          </div>

          <p className="text-slate-600 leading-relaxed mb-4">
            Sworn officers carry higher salary, overtime, and benefit costs.
            Traffic enforcement is a major source of administrative workload
            and overtime expenditure. The TNV division reduces this burden by
            assigning routine enforcement to personnel whose compensation
            reflects the risk level of the assignment.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border-2 border-[#002B7F]/10 p-5">
              <p className="text-xs text-slate-500 font-bold uppercase mb-1">
                TNV Officer Starting Salary
              </p>
              <p className="text-2xl font-black text-[#D4AF37]">
                $48K–$55K
              </p>
              <p className="text-slate-600 text-xs mt-1">
                Routine traffic enforcement
              </p>
            </div>
            <div className="bg-white border-2 border-[#002B7F]/10 p-5">
              <p className="text-xs text-slate-500 font-bold uppercase mb-1">
                Sworn Officer Starting Salary
              </p>
              <p className="text-2xl font-black text-[#002B7F]">$66K+</p>
              <p className="text-slate-600 text-xs mt-1">
                Violent crime, investigations, emergency response (plus
                overtime and benefits)
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-8">
            {[
              "More sworn officer time spent on violent crime and investigations",
              "Reduced overtime burden on sworn police departments",
              "Improved allocation of public safety resources",
              "Lower per-stop enforcement cost for routine violations",
              "Budget savings redirected to critical public safety priorities",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 bg-white border-2 border-[#002B7F]/10 p-4"
              >
                <TrendingUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-slate-700 font-medium text-sm">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 7: LEGAL AND ACCOUNTABILITY ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Scale className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Legal and Accountability Impact
            </h2>
          </div>

          <p className="text-slate-600 leading-relaxed mb-4">
            Traffic stops are a frequent source of civil litigation claims.
            Many claims relate to discretionary enforcement decisions,
            escalations, and allegations of inconsistent enforcement. This
            represents significant liability exposure for departments and
            municipalities.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                icon: BarChart3,
                title: "Standardized Enforcement",
                desc: "Removing discretionary stop selection reduces the variability that drives litigation",
              },
              {
                icon: FileText,
                title: "Objective Logging",
                desc: "Every violation basis is pre-recorded, creating clear documentation that reduces disputes",
              },
              {
                icon: Eye,
                title: "Full Audit Trails",
                desc: "Complete transparency and accountability for every enforcement action taken",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-5"
                >
                  <Icon className="w-6 h-6 text-[#002B7F] mb-3" />
                  <h3 className="font-bold text-[#002B7F] text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-5 mb-6">
            <p className="text-slate-500 text-xs">
              <strong>Sources:</strong> Bureau of Justice Statistics; Stanford
              Open Policing Project
            </p>
          </div>

          <div className="bg-[#D4AF37]/15 border-2 border-[#D4AF37]/30 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              Millions in potential reduced liability exposure through
              standardized, objective, and fully documented enforcement.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: BIAS AND CONSISTENCY ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Scale className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Bias and Consistency
            </h2>
          </div>

          <p className="text-white/80 leading-relaxed mb-6">
            Studies have shown variation in traffic stop and search outcomes
            across jurisdictions. This variation is widely linked to
            discretionary enforcement differences — individual officers making
            different decisions in similar situations. The TNV system reduces
            this discretionary variation through three mechanisms:
          </p>

          <div className="space-y-4 mb-8">
            {[
              {
                title: "Standardized Violation Detection",
                desc: "Technology identifies violations based on objective data — not officer judgment about which vehicles to stop",
              },
              {
                title: "Removed Subjective Stop Selection",
                desc: "The system determines which vehicles have verifiable violations. Officers respond to system alerts, not personal assessment",
              },
              {
                title: "Logged Enforcement Actions",
                desc: "Every action is recorded, creating accountability and enabling oversight of enforcement patterns across the entire system",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/10 border border-white/20 p-5"
              >
                <h3 className="font-bold text-white text-sm mb-1">
                  {item.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white/10 border-2 border-[#D4AF37]/40 p-6 text-center">
            <p className="text-[#D4AF37] font-bold">
              "This system reduces inconsistency by reducing reliance on
              individual discretion in low-level enforcement decisions."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING PRINCIPLE ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
          <p className="text-xl md:text-2xl font-bold text-[#002B7F] leading-relaxed">
            "This reform is about ensuring the right level of response for the
            right situation — so that routine enforcement remains consistent,
            low-risk, and efficient, while sworn officers focus on protecting
            the public from serious crime."
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Smarter Enforcement for a Safer Nevada
          </h2>
          <p className="text-[#C0C0C0] mb-8">
            Better allocation. Better outcomes. Better use of every public
            safety dollar.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/food-deserts"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Ending Food Deserts and Food Swamps <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TNVPage;
